/**
 * Thin analytics wrapper. Today this is a no-op + dev console.log + a push to
 * `window.dataLayer` (which a GTM container can consume later). The intent is
 * that *call sites* are stable now — switching to Mixpanel/PostHog/Segment is
 * a one-file change inside this wrapper.
 */

type TrackProps = Record<string, unknown>;

interface MaybeDataLayer {
  dataLayer?: { push: (entry: { event: string } & TrackProps) => void };
}

/**
 * Fire a product analytics event.
 *
 * @param event  Snake_case event name; keep it stable across releases.
 * @param props  Optional event properties. Avoid putting PII here.
 */
export function track(event: string, props: TrackProps = {}): void {
  try {
    if (typeof window === "undefined") return;

    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.log("[analytics]", event, props);
    }

    const w = window as unknown as MaybeDataLayer;
    if (w.dataLayer && typeof w.dataLayer.push === "function") {
      w.dataLayer.push({ event, ...props });
    }
  } catch {
    // Swallow — analytics must never break a user flow.
  }
}

/** Canonical event-name constants — keep here so call sites grep cleanly. */
export const AnalyticsEvents = {
  BrochureViewed: "brochure_viewed",
  /*
    CO-227 — `StartTrialCtaClicked` is retired and replaced by `SeePlansCtaClicked`. A NEW event
    name on purpose: the old one is already in the analytics history with a meaning ("this user
    asked for a trial") that no longer exists, and silently reusing it would make the funnel
    un-analysable across the cutover date.
  */
  SeePlansCtaClicked: "see_plans_cta_clicked",
  SignupFreeSubmitted: "signup_free_submitted",
  // CO-227 — `TrialStarted` removed; nothing can emit it.
  FeatureLockedHit: "feature_locked_hit",
  UpgradeRequestSubmitted: "upgrade_request_submitted",
  NudgeHrSubmitted: "nudge_hr_submitted",
  OperatorMarkPaid: "operator_mark_paid",
  OperatorRevoke: "operator_revoke",
  OperatorBulkMarkPaid: "operator_bulk_mark_paid",
  OperatorBulkRevoke: "operator_bulk_revoke",
  ReferenceCreated: "reference_created",
  ReferenceUpdated: "reference_updated",
  ReferenceDeactivated: "reference_deactivated",
  /*
    Boot diagnostics. These exist because the "shell renders with an empty user
    slice" failure emitted NOTHING — no request, no 4xx, no console error — and
    cost a full investigation to explain from a code read alone.

    - BootStateInvalid: signed in, but userId/role are empty AFTER boot finished.
    - BootUserInfo:     every boot Account/GetUserInfo attempt, with {ms, ok}.
                        This is what tells a slow success apart from a reject or
                        a true hang; the code alone could not.
    - SubscriptionMeFailed: /api/Subscription/me failed, with the HTTP status.
                        Previously swallowed, so a Paid tenant getting the
                        Free-tier paywall produced zero telemetry.
  */
  BootStateInvalid: "boot_state_invalid",
  BootUserInfo: "boot_get_user_info",
  SubscriptionMeFailed: "subscription_me_failed",
} as const;
